// src/components/ExportDashboard.js
import React, { useState, useEffect } from 'react';
import ExportExcelButton from './ExportExcelButton';
import { getProperties } from '../api';

const ExportDashboard = () => {
    const [properties, setProperties] = useState([]);
    const [selectedProperties, setSelectedProperties] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterType, setFilterType] = useState('all');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        fetchProperties();
    }, []);

    const fetchProperties = async () => {
        try {
            setLoading(true);
            const response = await getProperties();
            setProperties(response.data || []);
        } catch (err) {
            setError('Failed to load properties: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedProperties(properties.map(p => p._id));
        } else {
            setSelectedProperties([]);
        }
    };

    const handleSelectProperty = (propertyId) => {
        if (selectedProperties.includes(propertyId)) {
            setSelectedProperties(selectedProperties.filter(id => id !== propertyId));
        } else {
            setSelectedProperties([...selectedProperties, propertyId]);
        }
    };

    // Filter properties
    const filteredProperties = properties.filter(prop => {
        const matchesSearch = prop.location?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                             prop.sub_locality?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                             prop.property_type?.toLowerCase().includes(searchTerm.toLowerCase());
        
        const matchesType = filterType === 'all' || prop.property_type === filterType;
        
        return matchesSearch && matchesType;
    });

    // Get unique property types for filter
    const propertyTypes = ['all', ...new Set(properties.map(p => p.property_type).filter(Boolean))];

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading properties...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="p-6 max-w-7xl mx-auto">
            <div className="bg-white rounded-lg shadow-lg p-6">
                {/* Header */}
                <div className="flex justify-between items-center mb-6">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-800">📊 Export Properties</h1>
                        <p className="text-gray-600 mt-1">Select properties and export to Excel for client review</p>
                    </div>
                    <div className="flex gap-3">
                        {selectedProperties.length > 0 && (
                            <ExportExcelButton 
                                propertyIds={selectedProperties}
                                label={`📊 Export Selected (${selectedProperties.length})`}
                                variant="comparison"
                                className="bg-green-500 hover:bg-green-600"
                            />
                        )}
                        <button
                            onClick={() => window.location.href = '/properties'}
                            className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded"
                        >
                            ← Back to Properties
                        </button>
                    </div>
                </div>

                {/* Error Message */}
                {error && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
                        {error}
                    </div>
                )}

                {/* Search and Filter */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">🔍 Search</label>
                        <input
                            type="text"
                            placeholder="Search by building name, locality, or type..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="border border-gray-300 rounded-md px-4 py-2 w-full focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">🏷️ Filter by Type</label>
                        <select
                            value={filterType}
                            onChange={(e) => setFilterType(e.target.value)}
                            className="border border-gray-300 rounded-md px-4 py-2 w-full focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                            {propertyTypes.map(type => (
                                <option key={type} value={type}>
                                    {type === 'all' ? 'All Types' : type}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <div className="bg-blue-50 rounded-lg p-4">
                        <div className="text-sm text-blue-600">Total Properties</div>
                        <div className="text-2xl font-bold text-blue-700">{properties.length}</div>
                    </div>
                    <div className="bg-green-50 rounded-lg p-4">
                        <div className="text-sm text-green-600">Selected</div>
                        <div className="text-2xl font-bold text-green-700">{selectedProperties.length}</div>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-4">
                        <div className="text-sm text-purple-600">Filtered Results</div>
                        <div className="text-2xl font-bold text-purple-700">{filteredProperties.length}</div>
                    </div>
                </div>

                {/* Property Table */}
                <div className="overflow-x-auto border rounded-lg">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-3 text-left">
                                    <input
                                        type="checkbox"
                                        onChange={handleSelectAll}
                                        checked={filteredProperties.length > 0 && 
                                                filteredProperties.every(p => selectedProperties.includes(p._id))}
                                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                    />
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Building / Location
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Type
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Price
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Area
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Status
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Action
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {filteredProperties.length === 0 ? (
                                <tr>
                                    <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                                        No properties found matching your criteria
                                    </td>
                                </tr>
                            ) : (
                                filteredProperties.map((property) => (
                                    <tr key={property._id} className="hover:bg-gray-50">
                                        <td className="px-4 py-3">
                                            <input
                                                type="checkbox"
                                                checked={selectedProperties.includes(property._id)}
                                                onChange={() => handleSelectProperty(property._id)}
                                                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                            />
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="font-medium text-gray-900">
                                                {property.location || 'Unnamed'}
                                            </div>
                                            <div className="text-sm text-gray-500">
                                                {property.sub_locality || 'N/A'}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">
                                                {property.property_type || 'N/A'}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 font-medium text-gray-900">
                                            {property.price || 'Price on Request'}
                                        </td>
                                        <td className="px-4 py-3 text-gray-600">
                                            {property.carpet_area || 'N/A'}
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className={`px-2 py-1 text-xs rounded-full ${
                                                property.status === 'approved' 
                                                    ? 'bg-green-100 text-green-800' 
                                                    : property.status === 'rejected'
                                                    ? 'bg-red-100 text-red-800'
                                                    : 'bg-yellow-100 text-yellow-800'
                                            }`}>
                                                {property.status || 'pending'}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3">
                                            <ExportExcelButton 
                                                propertyId={property._id}
                                                label="📊"
                                                variant="single"
                                                className="text-sm bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded"
                                            />
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Footer */}
                <div className="mt-6 flex justify-between items-center">
                    <div className="text-sm text-gray-500">
                        {selectedProperties.length} of {properties.length} properties selected
                    </div>
                    <div className="flex gap-3">
                        <button
                            onClick={() => setSelectedProperties([])}
                            className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50"
                        >
                            Clear Selection
                        </button>
                        {selectedProperties.length > 0 && (
                            <ExportExcelButton 
                                propertyIds={selectedProperties}
                                label={`📊 Export ${selectedProperties.length} Properties`}
                                variant="comparison"
                                className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded"
                            />
                        )}
                    </div>
                </div>

                {/* Quick Export Section */}
                <div className="mt-8 border-t pt-6">
                    <h3 className="text-lg font-semibold text-gray-800 mb-3">⚡ Quick Actions</h3>
                    <div className="flex flex-wrap gap-3">
                        {selectedProperties.length === 0 && properties.length > 0 && (
                            <>
                                <ExportExcelButton 
                                    propertyIds={properties.slice(0, 10).map(p => p._id)}
                                    label="📊 Export First 10 Properties"
                                    variant="comparison"
                                    className="bg-purple-500 hover:bg-purple-600 text-white px-4 py-2 rounded"
                                />
                                <button
                                    onClick={() => {
                                        const allIds = properties.map(p => p._id);
                                        setSelectedProperties(allIds);
                                    }}
                                    className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded"
                                >
                                    Select All {properties.length} Properties
                                </button>
                            </>
                        )}
                        <button
                            onClick={fetchProperties}
                            className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded"
                        >
                            🔄 Refresh
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ExportDashboard;